import { useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import type { ZodTypeAny } from 'zod'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Form } from '@app/components/core/form/Form'
import { TextField } from '@app/components/core/form/fields/TextField'
import { TextAreaField } from '@app/components/core/form/fields/TextAreaField'
import { ToggleField } from '@app/components/core/form/fields/ToggleField'
import { DateTimeSplitField } from '@app/components/core/form/fields/DateTimeSplitField'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { CreationGuide } from '@app/pages/dashboard/initiative/create/blocks/CreationGuide'
import { useTranslations } from '@i18n/useLanguage'
import { type GeneralInfoFormValues } from '../shared/eventValidation'
import { eventCreationSteps, type EventCreationStep } from '../shared/workflow'
import { StepValuesSync } from '../shared/StepValuesSync'
import { Toggle } from '@app/components/core/toggle/Toggle'
import CalendarIllustration from '@app/assets/images/illustrations/calendar.png'
import { Image } from '@app/components/core/image/Image'

const CURRENT_STEP: EventCreationStep = eventCreationSteps.generalInformation

type Props = {
  schema: ZodTypeAny
  defaultValues: GeneralInfoFormValues
  onValuesChange: (values: GeneralInfoFormValues) => void
}

const isMultidayFromDates = (start: string, end: string) => {
  const startDate = start.split('T')[0]
  const endDate = end.split('T')[0]

  if (!startDate || !endDate) {
    return false
  }

  return startDate !== endDate
}

const combine = (date: string, time: string) =>
  date && time ? `${date}T${time}` : date

/**
 * Single-day inputs: one shared date plus a start and end time. The date drives
 * both `start` and `end` so the end date always tracks the start date; end time
 * is optional. Writes straight to the `start` / `end` form values, keeping the
 * data model a pair of datetime strings.
 */
const SingleDayDateTimeFields = () => {
  const t = useTranslations().app.dashboard.event.creation
  const {
    setValue,
    formState: { errors },
  } = useFormContext<GeneralInfoFormValues>()
  const start = useWatch<GeneralInfoFormValues, 'start'>({ name: 'start' })
  const end = useWatch<GeneralInfoFormValues, 'end'>({ name: 'end' })

  const [date = '', startTime = ''] = (start ?? '').split('T')
  const [, endTime = ''] = (end ?? '').split('T')

  return (
    <Column gap="1rem" alignItems="stretch">
      <TextInput
        label={t.dateStartLabel}
        placeholder={t.dateStartPlaceholder}
        inputType="date"
        required
        text={date}
        setText={(d) => {
          setValue('start', combine(d, startTime), { shouldValidate: true })

          if (endTime) {
            setValue('end', combine(d, endTime), { shouldValidate: true })
          }
        }}
        errorMessage={errors.start?.message}
      />
      <Row gap="1rem" style={{ alignItems: 'stretch' }}>
        <TextInput
          label={t.startTimeLabel}
          placeholder={t.startTimePlaceholder}
          inputType="time"
          required
          text={startTime.substring(0, 5)}
          setText={(tm) =>
            setValue('start', combine(date, tm), { shouldValidate: true })
          }
          style={{ flex: 1 }}
          step={900}
        />
        <TextInput
          label={t.endTimeLabel}
          placeholder={t.endTimePlaceholder}
          inputType="time"
          text={endTime.substring(0, 5)}
          setText={(tm) =>
            setValue('end', tm ? combine(date, tm) : '', {
              shouldValidate: true,
            })
          }
          errorMessage={errors.end?.message}
          style={{ flex: 1 }}
          step={900}
        />
      </Row>
    </Column>
  )
}

/**
 * Date/time section: a one-day / multiday toggle that reveals the end field.
 * Both branches write directly to the `start` / `end` form values, so the data
 * model stays a pair of datetime strings.
 */
const EventDateTimeFields = ({
  isMultidayDefault,
}: {
  isMultidayDefault: boolean
}) => {
  const t = useTranslations().app.dashboard.event.creation
  const { setValue, getValues } = useFormContext<GeneralInfoFormValues>()
  const [isMultiday, setIsMultiday] = useState(isMultidayDefault)

  const durationOptions = [
    { label: t.singleDay, value: false },
    { label: t.multiDay, value: true },
  ]

  return (
    <Column gap="1.5rem" alignItems="stretch">
      <Toggle<boolean>
        style={{ alignSelf: 'stretch' }}
        options={durationOptions}
        selectedOption={isMultiday}
        setSelectedOption={(value) => {
          setIsMultiday(value)
          if (!value) {
            const startVal = getValues('start')
            const endVal = getValues('end')

            if (startVal && endVal) {
              const [startDate] = startVal.split('T')
              const [, endTime] = endVal.split('T')

              if (startDate && endTime) {
                setValue('end', `${startDate}T${endTime}`, {
                  shouldValidate: true,
                })

                return
              }
            }
            setValue('end', '', { shouldValidate: true })
          }
        }}
      />
      {!isMultiday ? (
        <SingleDayDateTimeFields />
      ) : (
        <>
          <DateTimeSplitField<GeneralInfoFormValues>
            name="start"
            dateLabel={t.dateStartLabel}
            datePlaceholder={t.dateStartPlaceholder}
            timeLabel={t.startTimeLabel}
            timePlaceholder={t.startTimePlaceholder}
            timeStep={900}
          />
          <DateTimeSplitField<GeneralInfoFormValues>
            name="end"
            dateLabel={t.dateEndLabel}
            datePlaceholder={t.dateEndPlaceholder}
            timeLabel={t.endTimeLabel}
            timePlaceholder={t.endTimePlaceholder}
            timeStep={900}
          />
        </>
      )}
    </Column>
  )
}

export const GeneralInformationStep = ({
  schema,
  defaultValues,
  onValuesChange,
}: Props) => {
  const t = useTranslations().app.dashboard.event.creation

  const stepTitles = { [CURRENT_STEP]: t.steps.generalInformation.title }
  const stepDescriptions = {
    [CURRENT_STEP]: t.steps.generalInformation.description,
  }

  const isMultidayDefault = isMultidayFromDates(
    defaultValues.start ?? '',
    defaultValues.end ?? '',
  )

  const eventTypeOptions = [
    { label: t.groupEvent, value: true },
    { label: t.oneOnOne, value: false },
  ]

  return (
    <CreationGuide
      steps={[CURRENT_STEP]}
      currentStep={CURRENT_STEP}
      stepTitles={stepTitles}
      stepDescriptions={stepDescriptions}
      illustration={
        <Image
          src={CalendarIllustration}
          alt="Calendar Illustration"
          style={{ maxWidth: '15rem' }}
        />
      }
    >
      <Form<GeneralInfoFormValues>
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <Column gap="2rem" alignItems="stretch" style={{ marginTop: '1.5rem' }}>
          <StepValuesSync<GeneralInfoFormValues>
            onValuesChange={onValuesChange}
          />
          <ToggleField
            options={eventTypeOptions}
            name="isGroupEvent"
            label={t.isGroupEventLabel}
          />
          <TextField<GeneralInfoFormValues>
            name="title"
            label={t.titleLabel}
            placeholder={t.titlePlaceholder}
          />
          <TextField<GeneralInfoFormValues>
            name="intro"
            label={t.introLabel}
            placeholder={t.introPlaceholder}
          />
          <TextAreaField<GeneralInfoFormValues>
            name="content"
            label={t.contentLabel}
            placeholder={t.contentPlaceholder}
          />
          <EventDateTimeFields isMultidayDefault={isMultidayDefault} />
        </Column>
      </Form>
    </CreationGuide>
  )
}
